import React from 'react';
import { graphql } from 'gatsby';
import WithSection from '../../components/WithSection/WithSection';
import {
    Body1,
    Head3,
    Subheading1,
} from '../../components/Typography/Typography';
import Box from '../../components/Box/Box';
import Image from '../../components/Image/Image';
import CtaSlots from '../../slots/CtaSlots/CtaSlots';
import { rem } from 'polished';
import PrismicLink from '../../components/PrismicLink/PrismicLink';
import Button from '../../components/Button/Button';
import ChevronRight from '../../images/icons/chevron-right.svg';
import { css } from 'styled-components';
import { theme } from '../../../config/theme';

const AddonsSection = ({ data: { primary, items } }) => {
    const { addons_section_description, addons_section_title, cta, image } =
        primary;

    return (
        <Box
            gridColumn={{ _: '1/-1', lg: '2/-2', xl: '3/-3' }}
            display="flex"
            flexDirection="column"
        >
            <Box
                display="flex"
                flexDirection="column"
                justifyItems="center"
                maxWidth={rem(942)}
                mx="auto"
                textAlign="center"
            >
                <Head3 bold>{addons_section_title}</Head3>
                <Body1 mt="20px">{addons_section_description}</Body1>
            </Box>
            <Box
                mt={rem(24)}
                display="grid"
                gridTemplateColumns={{
                    _: '1fr',
                    sm: '1fr 1fr',
                    lg: '1fr 1fr 1fr',
                }}
                gridColumnGap={rem(16)}
                gridRowGap={rem(16)}
            >
                {items.map(
                    ({
                        card_description,
                        card_title,
                        card_icon,
                        cta_1_link,
                        cta_1_text,
                        cta_2_link,
                        cta_2_text,
                    }) => (
                        <Box
                            display="flex"
                            flexDirection="column"
                            width="100%"
                            backgroundColor="neutral.white"
                            padding={rem(24)}
                            borderRadius="sm"
                            transition="box-shadow 120ms ease"
                            css={css`
                                :hover {
                                    box-shadow: 0 0 0 2px
                                        ${theme.colors.primary['300']};
                                    }
                                }
                            `}
                        >
                            <Box
                                display="grid"
                                placeItems="center"
                                backgroundColor="primary.50"
                                height={rem(64)}
                                width={rem(64)}
                                borderRadius="md"
                            >
                                <Image
                                    img={card_icon}
                                    width="32px"
                                    height="32px"
                                />
                            </Box>
                            <Subheading1
                                fontWeight="600"
                                mt={rem(8)}
                                mb={rem(4)}
                            >
                                {card_title}
                            </Subheading1>
                            <Body1 color="primary.1600">
                                {card_description}
                            </Body1>
                            <Box mt="auto">
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    mt={rem(16)}
                                    gap={rem(8)}
                                >
                                    {cta_1_text && (
                                        <PrismicLink
                                            link={cta_1_link}
                                            css={css`
                                                flex-grow: 1;
                                            `}
                                        >
                                            <Button
                                                variant="tertiary"
                                                size="md"
                                                fullWidth
                                                endIcon={
                                                    <Box
                                                        as={ChevronRight}
                                                        size={rem(20)}
                                                        color="currentColor"
                                                    />
                                                }
                                            >
                                                {cta_1_text}
                                            </Button>
                                        </PrismicLink>
                                    )}
                                    {cta_2_text && (
                                        <PrismicLink
                                            link={cta_2_link}
                                            css={css`
                                                flex-grow: 1;
                                            `}
                                        >
                                            <Button
                                                variant={
                                                    cta_1_text
                                                        ? 'ghost-blue'
                                                        : 'tertiary'
                                                }
                                                size="md"
                                                fullWidth
                                                endIcon={
                                                    <Box
                                                        as={ChevronRight}
                                                        size={rem(20)}
                                                        color="currentColor"
                                                    />
                                                }
                                            >
                                                {cta_2_text}
                                            </Button>
                                        </PrismicLink>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    )
                )}
            </Box>
        </Box>
    );
};

export const _ = graphql`
    fragment ADDONS_SECTION on PrismicSectionDataBodyAddOns {
        id
        items {
            card_description
            card_icon {
                gatsbyImageData(layout: CONSTRAINED, height: 64, width: 64)
                alt
                url
            }
            card_title
            cta_1_link {
                ...PRISMIC_LINK_FRAGMENT
            }
            cta_1_text
            cta_2_link {
                ...PRISMIC_LINK_FRAGMENT
            }
            cta_2_text
        }
        primary {
            addons_section_description
            addons_section_title
        }
    }
`;

export default WithSection(AddonsSection);
